import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  useTheme,
  IconButton,
  Alert,
  Fade,
  Slide,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../../Store/Slices/userSlice";
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
} from "../../Store/api/authApi";
import { showGlobalToast } from "../../Components/ToastProvider";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VerifiedIcon from "@mui/icons-material/Verified";

export default function Auth() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [userExist, setUserExist] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");

  const [sendOtp, { isLoading: isSending }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(""), 5000);
    return () => clearTimeout(timeout);
  }, [error]);

  const normalizeDigits = (value) => {
    return String(value)
      .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
      .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
  };

  const normalizePhoneNumber = (value) => {
    let normalized = normalizeDigits(value).replace(/\D/g, "").trim();

    if (normalized.startsWith("0098")) {
      normalized = "0" + normalized.slice(4);
    } else if (normalized.startsWith("98")) {
      normalized = "0" + normalized.slice(2);
    }

    return normalized.slice(0, 11);
  };

  const normalizeOtpCode = (value) => {
    return normalizeDigits(value).replace(/\D/g, "").slice(0, 6);
  };

  const handleSendPhone = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedPhone = normalizePhoneNumber(phoneNumber);

    if (!normalizedPhone) {
      const message = "لطفاً شماره موبایل خود را وارد کنید";
      setError(message);
      showGlobalToast(message, "error");
      return;
    }

    if (!/^09\d{9}$/.test(normalizedPhone)) {
      const message = "شماره موبایل باید با 09 شروع شود و ۱۱ رقم باشد";
      setError(message);
      showGlobalToast(message, "error");
      return;
    }

    try {
      const res = await sendOtp({ phoneNumber: normalizedPhone }).unwrap();

      if (res.success) {
        setPhoneNumber(normalizedPhone);
        setUserExist(Boolean(res.data?.userExist));
        setStep(2);
        setTimer(120);
        setCode("");
        setFullName("");
        showGlobalToast("کد تایید برای شما ارسال شد");
      } else {
        const message =  "ارسال کد تایید با مشکل مواجه شد";
        setError(message);
        showGlobalToast(message, "error");
      }
    } catch (err) {
      const message =  "ارسال کد تایید با مشکل مواجه شد";
      setError(message);
      showGlobalToast(message, "error");
      console.error("Error sending OTP:", err);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    const normalizedCode = normalizeOtpCode(code);

    if (!normalizedCode) {
      const message = "لطفاً کد تایید را وارد کنید";
      setError(message);
      showGlobalToast(message, "error");
      return;
    }

    if (normalizedCode.length !== 6) {
      const message = "کد تایید باید ۶ رقم باشد";
      setError(message);
      showGlobalToast(message, "error");
      return;
    }

    if (!userExist && !fullName.trim()) {
      const message = "لطفاً نام و نام خانوادگی خود را وارد کنید";
      setError(message);
      showGlobalToast(message, "error");
      return;
    }

    try {
      const body = {
        phoneNumber: normalizedPhone,
        code: normalizedCode,
        ...(!userExist && { fullName: fullName.trim() }),
      };

      const res = await verifyOtp(body).unwrap();

      if (res.success) {
        dispatch(
          login({
            token: res.data.token,
            user: res.data.user,
          }),
        );
        showGlobalToast("با موفقیت وارد شدید");
        setTimeout(() => navigate("/"), 500);
      } else {
        const message = res.message || "تایید کد با مشکل مواجه شد";
        setError(message);
        showGlobalToast(message, "error");
      }
    } catch (err) {
      const message =   "تایید کد با مشکل مواجه شد";
      setError(message);
      showGlobalToast(message, "error");
      console.error("Error verifying OTP:", err);
      console.error("Verify payload:", {
        phoneNumber: normalizedPhone,
        code: normalizedCode,
        fullName: fullName.trim(),
      });
    }
  };

  const handleResend = async () => {
    setError("");

    const normalizedPhone = normalizePhoneNumber(phoneNumber);

    if (!/^09\d{9}$/.test(normalizedPhone)) {
      const message = "شماره موبایل معتبر نیست";
      setError(message);
      showGlobalToast(message, "error");
      setStep(1);
      return;
    }

    try {
      const res = await resendOtp({ phoneNumber: normalizedPhone }).unwrap();

      if (res.success) {
        setTimer(120);
        setCode("");
        showGlobalToast(res.message || "کد تایید دوباره ارسال شد");
      } else {
        const message = res.message || "ارسال مجدد کد با مشکل مواجه شد";
        setError(message);
        showGlobalToast(message, "error");
      }
    } catch (err) {
      const message = err?.data?.message || "ارسال مجدد با مشکل مواجه شد";
      setError(message);
      showGlobalToast(message, "error");
      console.error("Error resending OTP:", err);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleBackToPhone = () => {
    setStep(1);
    setCode("");
    setFullName("");
    setError("");
    setTimer(0);
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: { xs: 1.5, sm: 2 },
        position: "relative",
      }}
    >
      <IconButton
        onClick={handleBackToHome}
        aria-label="بازگشت به صفحه اصلی"
        sx={{
          position: "absolute",
          top: { xs: 12, sm: 20 },
          right: { xs: 12, sm: 20 },
          color: "text.secondary",
          bgcolor: "background.paper",
          boxShadow: 1,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          "&:hover": {
            bgcolor: "primary.main",
            color: "white",
            transform: "scale(1.1)",
          },
          transition: "all 0.3s ease",
          zIndex: 1,
        }}
      >
        <HomeIcon fontSize={isMobile ? "small" : "medium"} />
      </IconButton>

      <Fade in timeout={500}>
        <Card
          sx={{
            maxWidth: { xs: "100%", sm: 440 },
            width: "100%",
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: theme.shadows[20],
            p: { xs: 1.5, sm: 3 },
            bgcolor: "background.paper",
            position: "relative",
            overflow: "hidden",
            mx: { xs: 1, sm: 0 },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {step === 2 && (
              <IconButton
                onClick={handleBackToPhone}
                aria-label="بازگشت به مرحله ورود شماره موبایل"
                sx={{
                  mb: 2,
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    transform: "translateX(-4px)",
                  },
                  transition: "all 0.3s ease",
                  float: "left",
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Box
              textAlign="center"
              mb={3}
              sx={{
                clear: "both",
                pt: step === 2 ? 0 : 1,
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight={800}
                sx={{
                  color: "primary.main",
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {step === 1 ? (
                  <>
                    <PhoneAndroidIcon fontSize={isMobile ? "small" : "medium"} />
                    ورود / ثبت‌نام
                  </>
                ) : (
                  <>
                    <VerifiedIcon fontSize={isMobile ? "small" : "medium"} />
                    تایید شماره موبایل
                  </>
                )}
              </Typography>

              <Typography
                variant={isMobile ? "body2" : "body1"}
                color="text.secondary"
                sx={{ px: 1, lineHeight: 1.9 }}
              >
                {step === 1
                  ? "شماره موبایل خود را وارد کنید تا کد تایید برای شما ارسال شود"
                  : `کد ۶ رقمی ارسال‌شده به ${phoneNumber} را وارد کنید`}
              </Typography>
            </Box>

            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  onClose={() => setError("")}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    fontSize: isMobile ? "0.8rem" : "0.875rem",
                    textAlign: "right",
                    alignItems: "center",
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {step === 1 && (
              <Slide direction="right" in mountOnEnter unmountOnExit>
                <form onSubmit={handleSendPhone}>
                  <Stack spacing={3}>
                    <Stack spacing={1}>
                      <Typography
                        fontWeight={600}
                        fontSize={isMobile ? "0.85rem" : "0.9rem"}
                      >
                        شماره موبایل
                      </Typography>

                      <TextField
                        fullWidth
                        variant="outlined"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(normalizePhoneNumber(e.target.value));
                          setError("");
                        }}
                        placeholder="مثلاً 09123456789"
                        helperText="شماره موبایل باید با 09 شروع شود"
                        dir="ltr"
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: 11,
                          style: {
                            textAlign: "left",
                            direction: "ltr",
                          },
                        }}
                        size={isMobile ? "small" : "medium"}
                        disabled={isSending}
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: "right",
                            direction: "rtl",
                            mx: 0,
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            fontSize: isMobile ? "0.9rem" : "1rem",
                            "&.Mui-focused": {
                              boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                            },
                          },
                        }}
                      />
                    </Stack>

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size={isMobile ? "medium" : "large"}
                      disabled={isSending || phoneNumber.length !== 11}
                      sx={{
                        borderRadius: 2,
                        py: { xs: 1.5, sm: 1.8 },
                        fontWeight: 700,
                        fontSize: isMobile ? "0.9rem" : "1rem",
                      }}
                    >
                      {isSending ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "دریافت کد تایید"
                      )}
                    </Button>
                  </Stack>
                </form>
              </Slide>
            )}

            {step === 2 && (
              <Slide direction="left" in mountOnEnter unmountOnExit>
                <form onSubmit={handleVerifyOtp}>
                  <Stack spacing={3}>
                    <Stack spacing={1}>
                      <Typography
                        fontWeight={600}
                        fontSize={isMobile ? "0.85rem" : "0.9rem"}
                      >
                        کد تایید
                      </Typography>

                      <TextField
                        fullWidth
                        variant="outlined"
                        value={code}
                        onChange={(e) => {
                          setCode(normalizeOtpCode(e.target.value));
                          setError("");
                        }}
                        placeholder="کد ۶ رقمی"
                        helperText="کد ارسال‌شده را به صورت عددی وارد کنید"
                        dir="ltr"
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: 6,
                          style: {
                            textAlign: "center",
                            direction: "ltr",
                            letterSpacing: isMobile ? "4px" : "8px",
                            fontSize: isMobile ? "1.2rem" : "1.5rem",
                            fontWeight: 600,
                          },
                        }}
                        disabled={isVerifying}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: "right",
                            direction: "rtl",
                            mx: 0,
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&.Mui-focused": {
                              boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                            },
                          },
                        }}
                      />
                    </Stack>

                    {!userExist && (
                      <Fade in>
                        <Stack spacing={1}>
                          <Typography
                            fontWeight={600}
                            fontSize={isMobile ? "0.85rem" : "0.9rem"}
                          >
                            نام و نام خانوادگی
                          </Typography>

                          <TextField
                            fullWidth
                            variant="outlined"
                            value={fullName}
                            onChange={(e) => {
                              setFullName(e.target.value);
                              setError("");
                            }}
                            placeholder="مثلاً علی محمدی"
                            helperText="این نام برای ساخت حساب کاربری استفاده می‌شود"
                            disabled={isVerifying}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              "& .MuiFormHelperText-root": {
                                textAlign: "right",
                                direction: "rtl",
                                mx: 0,
                              },
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&.Mui-focused": {
                                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                                },
                              },
                            }}
                          />
                        </Stack>
                      </Fade>
                    )}

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size={isMobile ? "medium" : "large"}
                      disabled={
                        isVerifying ||
                        code.length !== 6 ||
                        (!userExist && !fullName.trim())
                      }
                      sx={{
                        borderRadius: 2,
                        py: { xs: 1.5, sm: 1.8 },
                        fontWeight: 700,
                        fontSize: isMobile ? "0.9rem" : "1rem",
                      }}
                    >
                      {isVerifying ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "تایید و ورود"
                      )}
                    </Button>

                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: "background.default",
                        borderRadius: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBackToPhone}
                        sx={{
                          fontSize: isMobile ? "0.8rem" : "0.85rem",
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                        disabled={isVerifying}
                        fullWidth={isMobile}
                      >
                        تغییر شماره موبایل
                      </Button>

                      {timer > 0 ? (
                        <Box
                          dir="ltr"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CircularProgress
                            size={isMobile ? 16 : 20}
                            variant="determinate"
                            value={(timer / 120) * 100}
                            color="primary"
                            thickness={5}
                          />
                          <Typography
                            variant={isMobile ? "caption" : "body2"}
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ direction: "ltr" }}
                          >
                            {Math.floor(timer / 60)}:
                            {String(timer % 60).padStart(2, "0")}
                          </Typography>
                        </Box>
                      ) : (
                        <Button
                          variant="text"
                          color="secondary"
                          onClick={handleResend}
                          disabled={isResending}
                          sx={{
                            fontSize: isMobile ? "0.8rem" : "0.85rem",
                            fontWeight: 600,
                            "&:hover": {
                              bgcolor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                          fullWidth={isMobile}
                        >
                          {isResending ? (
                            <CircularProgress size={isMobile ? 16 : 18} />
                          ) : (
                            "ارسال مجدد کد"
                          )}
                        </Button>
                      )}
                    </Paper>
                  </Stack>
                </form>
              </Slide>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
}
